'use client'
import { Button, Divider, Flex, Menu, Text, Title } from '@mantine/core'
import { useContext, createContext } from 'react'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import type { MouseEvent } from 'react'

import { accountRoute, meRoute } from '~/utils/routes'
import { WaitForStableAuthState, useAppAuth } from '../../auth'
import { AppLayoutContext, IdentityContext } from '../../context'
import { SafeAvatarImage } from '../../images'
import { Route } from 'next'
import { appTrace } from '~/utils'
import { useNavigation } from '../../auth/NavigationContext'
import { navbarItem, noUnderline } from './styles.css'
import { DesktopMenuDrawer } from './HeaderDrawer'
import { getDisplayName } from '~/app/lib/GetAccount'

// Types
type NavPropsType = {
  openedNav: boolean
  closeNav: () => void
  openNav: () => void
}

type MenuItemProps = {
  path: Route
  label: string
  onClick?: () => void
}

type AccountProfileProps = {
  onClick?: () => void
  variant?: 'current' | 'other'
}

type ExternalLinkProps = {
  label: string
  href: string
}

type DrawerContentProps = {
  close: () => void
}

// Context
const CloseContext = createContext<(() => void) | null>(null)

// Constants
const EXTERNAL_LINKS = [
  { label: 'How Dome Works', href: 'https://www.createwithdome.com/how-dome-works' },
  { label: 'Become a beta tester', href: 'https://www.createwithdome.com/become-a-beta-tester' },
  { label: 'Contact', href: 'https://www.createwithdome.com/contact' },
] as const

const SIGN_IN_BUTTON_STYLE = {
  cursor: 'pointer',
  backgroundColor: '#48FFB9',
  color: 'black',
  border: '1px solid black',
  fontSize: '12px',
  borderRadius: '8px',
  fontWeight: '600',
} as const

// Common Components
const CommonNavigationMenuItem = ({ path, label, onClick }: MenuItemProps) => {
  const navigation = useNavigation()
  const close = useContext(CloseContext)
  const { mobile } = useContext(AppLayoutContext)
  const testId = `account_nav_${label.toLowerCase().replace(/\s+/g, '_')}`
  const name = testId

  const handleClick = (e: MouseEvent) => {
    if (onClick) {
      onClick()
    } else {
      navigation.navigate({ name, route: path, event: e })
    }
    close?.()
  }

  return (
    <Menu.Item
      onClick={handleClick}
      data-testid={testId}
      style={{
        padding: '0',
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: 'transparent',
        },
      }}
    >
      <Title
        tt="uppercase"
        order={mobile ? 2 : 1}
        style={{ letterSpacing: mobile ? '-1px' : '-2px' }}
        className={navbarItem}
      >
        {label}
      </Title>
    </Menu.Item>
  )
}

const CommonExternalNavigationLink = ({ label, href }: ExternalLinkProps) => (
  <a href={href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
    <Title order={4} tt="uppercase">
      {label}
    </Title>
  </a>
)

const CommonUserProfileButton = ({ onClick, variant = 'current' }: AccountProfileProps) => {
  const navigation = useNavigation()
  const close = useContext(CloseContext)
  const { currentAccount } = useContext(IdentityContext)
  const { mobile } = useContext(AppLayoutContext)

  if (!currentAccount) return null

  const handleProfileClick = () => {
    if (onClick) {
      onClick()
    } else {
      navigation.navigate({
        name: 'account_nav_profile',
        route: accountRoute(currentAccount.id).show,
      })
    }
    close?.()
  }

  return (
    <Flex
      direction={mobile ? 'column' : 'row'}
      gap={mobile ? 'sm' : '5px'}
      align={mobile ? 'center' : 'flex-start'}
      mb={mobile ? '50px' : undefined}
      onClick={handleProfileClick}
      data-testid="account-nav-profile"
    >
      <SafeAvatarImage radius={mobile ? 'xsm' : 'sm'} account={currentAccount} />
      {variant === 'current' && (
        <Flex direction="column">
          <Title
            tt="uppercase"
            order={mobile ? 2 : 1}
            className={navbarItem}
            lh="77%"
            ta={mobile ? 'center' : undefined}
            lts={mobile ? '-1px' : '-2px'}
            mb={mobile ? undefined : '5px'}
          >
            {getDisplayName(currentAccount)}
          </Title>
          <Title order={6} tt="uppercase" className={noUnderline} ta={mobile ? 'center' : undefined}>
            {currentAccount.type === 'Personal' ? 'Individual Account' : 'Business Account'}
          </Title>
        </Flex>
      )}
    </Flex>
  )
}

// Mobile Components
const MobileSignInButton = () => {
  const { handleSignIn } = useAppAuth()

  return (
    <Flex align="center" gap="sm">
      <Button px="xs" py="4px" style={SIGN_IN_BUTTON_STYLE} onClick={handleSignIn} data-testid="signin-button">
        SIGN IN/SIGN UP
      </Button>
    </Flex>
  )
}

const MobileNavigationDrawerContent = ({ close }: DrawerContentProps) => {
  const { currentAccount } = useContext(IdentityContext)
  const { handleSignOut } = useAppAuth()
  const navigation = useNavigation()

  if (!currentAccount) {
    return (
      <CloseContext.Provider value={close}>
        <Flex direction="column" m="xl" h="100%" justify="space-between">
          <Menu>
            <Flex direction="column" gap="5px">
              <CommonNavigationMenuItem path="/" label="Community" />
              <CommonNavigationMenuItem path="/iso" label="In Search Of" />
            </Flex>
          </Menu>
          <Flex mt="50px" gap="5px" direction="column">
            {EXTERNAL_LINKS.map((link) => (
              <CommonExternalNavigationLink key={link.href} {...link} />
            ))}
          </Flex>
        </Flex>
      </CloseContext.Provider>
    )
  }

  return (
    <CloseContext.Provider value={close}>
      <Flex direction="column" m="xl" h="100%" justify="space-between">
        <CommonUserProfileButton
          variant="current"
          onClick={() => {
            appTrace('account_nav_click', { accountId: currentAccount.id })
            navigation.navigate({
              name: 'account_nav_profile',
              route: accountRoute(currentAccount.id).show,
            })
          }}
        />

        <Menu>
          <Flex direction="column" gap="5px">
            <CommonNavigationMenuItem path="/" label="Community" />
            <CommonNavigationMenuItem path="/iso" label="In Search Of" />
            <CommonNavigationMenuItem path="/iso/me" label="Your Shoots" />
            <CommonNavigationMenuItem path="/contacts" label="Contacts" />
            <a href={meRoute().editProfile} style={{ color: 'inherit', textDecoration: 'none' }}>
              <CommonNavigationMenuItem path={meRoute().editProfile} label="Settings" />
            </a>
            <CommonNavigationMenuItem
              path="/"
              label="Sign out"
              onClick={() => {
                handleSignOut()
              }}
            />
          </Flex>
        </Menu>

        <Flex mt="50px" gap="5px" direction="column">
          {EXTERNAL_LINKS.map((link) => (
            <CommonExternalNavigationLink key={link.href} {...link} />
          ))}
          <Title
            order={5}
            tt="uppercase"
            onClick={() => {
              navigation.navigate({ name: 'account_nav_review', route: '/review' })
            }}
          >
            Review Dome
          </Title>
        </Flex>
      </Flex>
    </CloseContext.Provider>
  )
}

// Desktop Components
const DesktopSignInButton = () => {
  const { handleSignIn } = useAppAuth()

  return (
    <Flex align="center" gap="xl">
      <a
        href="https://www.createwithdome.com/how-dome-works"
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: 'none', color: 'inherit' }}
      >
        <Title order={5} tt="uppercase">
          How Dome Works
        </Title>
      </a>
      <Button px="xs" py="4px" style={SIGN_IN_BUTTON_STYLE} onClick={handleSignIn} data-testid="signin-button">
        SIGN IN/SIGN UP
      </Button>
    </Flex>
  )
}

const DesktopNavigationDrawerContent = ({ close }: DrawerContentProps) => {
  const { currentAccount } = useContext(IdentityContext)
  const { handleSignOut } = useAppAuth()
  const navigation = useNavigation()

  if (!currentAccount) return null

  return (
    <CloseContext.Provider value={close}>
      <Flex direction="column" m="95px" h="100%" justify="space-between">
        <CommonUserProfileButton
          variant="current"
          onClick={() => {
            appTrace('account_nav_click', { accountId: currentAccount.id })
            navigation.navigate({
              name: 'account_nav_profile',
              route: accountRoute(currentAccount.id).show,
            })
          }}
        />

        <Divider my="xl" />

        <Menu>
          <Flex direction="column">
            <CommonNavigationMenuItem path="/" label="Community" />
            <CommonNavigationMenuItem path="/iso" label="In Search Of" />
            <CommonNavigationMenuItem path="/iso/me" label="Your Shoots" />
            <CommonNavigationMenuItem path="/contacts" label="Contacts" />
            <a href={meRoute().editProfile} style={{ color: 'inherit', textDecoration: 'none' }}>
              <CommonNavigationMenuItem path={meRoute().editProfile} label="Settings" />
            </a>
            <CommonNavigationMenuItem
              path="/"
              label="Sign out"
              onClick={() => {
                handleSignOut()
              }}
            />
          </Flex>
        </Menu>

        <Flex mt="50px" gap="5px" direction="column">
          {EXTERNAL_LINKS.map((link) => (
            <CommonExternalNavigationLink key={link.href} {...link} />
          ))}
          <Title
            order={4}
            tt="uppercase"
            onClick={() => {
              navigation.navigate({ name: 'account_nav_review', route: '/review' })
            }}
          >
            Review Dome
          </Title>
        </Flex>
      </Flex>
    </CloseContext.Provider>
  )
}

const DesktopAccountNav = ({ openedNav, closeNav, openNav }: NavPropsType) => {
  const { currentAccount } = useContext(IdentityContext)

  if (!currentAccount?.profile) return <DesktopSignInButton />

  return (
    <>
      <Button
        h="100%"
        data-testid="header-accountnav-button"
        style={{ backgroundColor: 'transparent' }}
        p="xs"
        pr="0px"
        mr="0px"
        onClick={openedNav ? closeNav : openNav}
      >
        <SafeAvatarImage radius="xxs" account={currentAccount} />
        <Text size="lg" fw={600} mx="xs" tt={'uppercase'}>
          {/* {currentAccount.profile.name} */}
          {getDisplayName(currentAccount)}
        </Text>
        {openedNav ? <ChevronUpIcon height={20} /> : <ChevronDownIcon height={20} />}
      </Button>
    </>
  )
}

export function AccountNav({ openedNav, closeNav, openNav }: NavPropsType) {
  const { mobile } = useContext(AppLayoutContext)
  const { currentAccount } = useContext(IdentityContext)

  return (
    <WaitForStableAuthState context="nav">
      {mobile ? (
        currentAccount?.profile ? null : (
          <MobileSignInButton />
        )
      ) : (
        <DesktopAccountNav openedNav={openedNav} closeNav={closeNav} openNav={openNav} />
      )}
    </WaitForStableAuthState>
  )
}

export const AccountNavDrawerContent = ({ close }: DrawerContentProps) => {
  const { mobile } = useContext(AppLayoutContext)
  return mobile ? <MobileNavigationDrawerContent close={close} /> : <DesktopNavigationDrawerContent close={close} />
}
